import { useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  FileText, Plus, Trash2, User, Calendar, DollarSign, ArrowLeft,
  ArrowRight, CheckCircle2, Download, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = (service) => ({
  client_nom: '', client_representant: '', client_email: '',
  client_telephone: '', client_adresse: '', client_ninea: '', client_rc: '',
  service_id: service?.id || '',
  service_titre: service?.titre || 'Prestation Conception',
  objet_contrat: '',
  lignes: [{ designation: '', quantite: 1, unite: 'forfait', prix_unitaire: 0 }],
  remise_pct: 0, tva_pct: 18, acompte_pct: 30,
  date_signature: new Date().toISOString().slice(0, 10),
  date_debut: '', date_livraison: '', duree_jours: 30,
  conditions_paiement: '30% à la signature, 70% à la livraison',
  penalites_retard: '1% par jour de retard',
  lieu_signature: 'Bangui',
  notes: '', statut: 'brouillon',
});

const fmt = (n) => `${Math.round(Number(n || 0)).toLocaleString('fr-FR').replace(/,/g, ' ')} FCFA`;

export default function FaireContratSheet({ open, onOpenChange, service }) {
  const [form, setForm] = useState(() => emptyForm(service));
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [savedContract, setSavedContract] = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setLigne = (idx, k, v) => {
    const next = [...form.lignes];
    next[idx] = { ...next[idx], [k]: v };
    set('lignes', next);
  };
  const addLigne = () => set('lignes', [...form.lignes, { designation: '', quantite: 1, unite: 'forfait', prix_unitaire: 0 }]);
  const removeLigne = (i) => set('lignes', form.lignes.filter((_, j) => j !== i));

  const totals = (() => {
    const st = form.lignes.reduce((s, l) => s + Number(l.prix_unitaire || 0) * Number(l.quantite || 0), 0);
    const remise = st * (Number(form.remise_pct || 0) / 100);
    const ht = st - remise;
    const tva = ht * (Number(form.tva_pct || 0) / 100);
    const ttc = ht + tva;
    const acompte = ttc * (Number(form.acompte_pct || 0) / 100);
    return { st, remise, ht, tva, ttc, acompte, solde: ttc - acompte };
  })();

  const reset = () => {
    setForm(emptyForm(service));
    setStep(1);
    setSavedContract(null);
  };

  const save = async () => {
    if (!form.client_nom.trim()) { toast.error('Le nom du client est requis'); setStep(1); return; }
    if (!form.lignes.some(l => l.designation && Number(l.quantite) > 0)) {
      toast.error('Ajoutez au moins une prestation'); setStep(2); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lignes: form.lignes.map(l => ({ ...l, quantite: Number(l.quantite || 0), prix_unitaire: Number(l.prix_unitaire || 0) })),
        remise_pct: Number(form.remise_pct || 0),
        tva_pct: Number(form.tva_pct || 0),
        acompte_pct: Number(form.acompte_pct || 0),
        duree_jours: Number(form.duree_jours || 0),
      };
      const res = await axios.post(`${API}/api/contrats-conception`, payload);
      toast.success(`Contrat ${res.data.numero} créé`);
      setSavedContract(res.data);
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    if (!savedContract) return;
    try {
      const r = await axios.get(`${API}/api/contrats-conception/${savedContract.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `contrat-conception-${savedContract.numero}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch { toast.error('Erreur PDF'); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <SheetContent side="right" className="sm:max-w-2xl w-full overflow-y-auto p-0" data-testid="faire-contrat-sheet">
        <SheetHeader className="p-5 border-b sticky top-0 bg-white z-10">
          <SheetTitle className="text-xl text-[#0B1E42] flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Nouveau contrat – {service?.titre || 'Prestation'}
          </SheetTitle>
          {/* Stepper */}
          <div className="flex items-center gap-2 mt-3">
            {[1, 2, 3, 4].map((n, i) => (
              <div key={n} className="flex-1 flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step === n ? 'bg-orange-500 text-white' :
                    step > n ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > n ? <CheckCircle2 className="h-4 w-4" /> : n}
                </div>
                <span className={`text-xs font-semibold ${step === n ? 'text-orange-600' : step > n ? 'text-emerald-700' : 'text-gray-500'}`}>
                  {['Client', 'Prestations', 'Conditions', 'PDF'][i]}
                </span>
                {i < 3 && <div className={`flex-1 h-0.5 ${step > n ? 'bg-emerald-500' : 'bg-gray-300'}`} />}
              </div>
            ))}
          </div>
        </SheetHeader>

        <div className="p-5 space-y-4">
          {/* Step 1 : Client */}
          {step === 1 && (
            <div className="space-y-3" data-testid="step-client">
              <h3 className="font-semibold text-[#0B1E42] flex items-center gap-2"><User className="h-4 w-4" /> Informations client</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Nom / Raison sociale *</Label>
                  <Input value={form.client_nom} onChange={e => set('client_nom', e.target.value)} placeholder="ACME SARL" data-testid="input-client-nom" />
                </div>
                <div><Label>Représentant légal</Label><Input value={form.client_representant} onChange={e => set('client_representant', e.target.value)} placeholder="M./Mme…" /></div>
                <div><Label>Téléphone</Label><Input value={form.client_telephone} onChange={e => set('client_telephone', e.target.value)} /></div>
                <div><Label>Email</Label><Input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} /></div>
                <div><Label>NINEA / N° fiscal</Label><Input value={form.client_ninea} onChange={e => set('client_ninea', e.target.value)} /></div>
                <div className="md:col-span-2"><Label>Adresse</Label><Textarea rows={2} value={form.client_adresse} onChange={e => set('client_adresse', e.target.value)} /></div>
                <div className="md:col-span-2"><Label>Objet du contrat</Label><Textarea rows={2} value={form.objet_contrat} onChange={e => set('objet_contrat', e.target.value)} placeholder="Ex : Charte graphique complète pour le lancement de la marque" /></div>
              </div>
            </div>
          )}

          {/* Step 2 : Prestations */}
          {step === 2 && (
            <div className="space-y-3" data-testid="step-prestations">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#0B1E42] flex items-center gap-2"><FileText className="h-4 w-4" /> Prestations &amp; tarifs</h3>
                <Button size="sm" variant="outline" onClick={addLigne} className="gap-1"><Plus className="h-3.5 w-3.5" /> Ajouter</Button>
              </div>
              <div className="space-y-2">
                {form.lignes.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 p-2 border rounded-lg bg-slate-50 items-end">
                    <div className="col-span-6"><Label className="text-xs">Désignation</Label><Input value={l.designation} onChange={e => setLigne(i, 'designation', e.target.value)} data-testid={`ligne-designation-${i}`} /></div>
                    <div className="col-span-2"><Label className="text-xs">Qté</Label><Input type="number" value={l.quantite} onChange={e => setLigne(i, 'quantite', e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">Unité</Label><Input value={l.unite} onChange={e => setLigne(i, 'unite', e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">P.U. (FCFA)</Label><Input type="number" value={l.prix_unitaire} onChange={e => setLigne(i, 'prix_unitaire', e.target.value)} /></div>
                    <div className="col-span-12 flex items-center justify-between text-xs pt-1">
                      <span className="text-gray-500">Total : <b className="text-[#0B1E42]">{fmt(Number(l.prix_unitaire || 0) * Number(l.quantite || 0))}</b></span>
                      {form.lignes.length > 1 && <button onClick={() => removeLigne(i)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div><Label>Remise (%)</Label><Input type="number" value={form.remise_pct} onChange={e => set('remise_pct', e.target.value)} /></div>
                <div><Label>TVA (%)</Label><Input type="number" value={form.tva_pct} onChange={e => set('tva_pct', e.target.value)} /></div>
                <div><Label>Acompte (%)</Label><Input type="number" value={form.acompte_pct} onChange={e => set('acompte_pct', e.target.value)} /></div>
              </div>
              <div className="mt-2 p-3 bg-slate-50 border rounded-lg text-xs space-y-1">
                <div className="flex justify-between"><span>Sous-total HT</span><b>{fmt(totals.st)}</b></div>
                <div className="flex justify-between"><span>Total HT</span><b>{fmt(totals.ht)}</b></div>
                <div className="flex justify-between"><span>TVA</span><b>{fmt(totals.tva)}</b></div>
                <div className="flex justify-between text-blue-600 pt-1 border-t"><span className="font-bold">Total TTC</span><b>{fmt(totals.ttc)}</b></div>
                <div className="flex justify-between text-emerald-700"><span>Acompte à la signature</span><b>{fmt(totals.acompte)}</b></div>
                <div className="flex justify-between text-gray-600"><span>Solde à la livraison</span><b>{fmt(totals.solde)}</b></div>
              </div>
            </div>
          )}

          {/* Step 3 : Conditions */}
          {step === 3 && (
            <div className="space-y-3" data-testid="step-conditions">
              <h3 className="font-semibold text-[#0B1E42] flex items-center gap-2"><Calendar className="h-4 w-4" /> Dates &amp; conditions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Date de signature</Label><Input type="date" value={form.date_signature} onChange={e => set('date_signature', e.target.value)} /></div>
                <div><Label>Lieu de signature</Label><Input value={form.lieu_signature} onChange={e => set('lieu_signature', e.target.value)} /></div>
                <div><Label>Date de démarrage</Label><Input type="date" value={form.date_debut} onChange={e => set('date_debut', e.target.value)} /></div>
                <div><Label>Date de livraison</Label><Input type="date" value={form.date_livraison} onChange={e => set('date_livraison', e.target.value)} /></div>
                <div><Label>Durée (jours)</Label><Input type="number" value={form.duree_jours} onChange={e => set('duree_jours', e.target.value)} /></div>
              </div>
              <div><Label>Conditions de paiement</Label><Textarea rows={2} value={form.conditions_paiement} onChange={e => set('conditions_paiement', e.target.value)} /></div>
              <div><Label>Pénalités de retard</Label><Input value={form.penalites_retard} onChange={e => set('penalites_retard', e.target.value)} /></div>
              <div><Label>Notes complémentaires</Label><Textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            </div>
          )}

          {/* Step 4 : Récap + PDF */}
          {step === 4 && savedContract && (
            <div className="space-y-4" data-testid="step-pdf">
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                  <div>
                    <div className="font-bold text-emerald-800">Contrat {savedContract.numero} créé avec succès !</div>
                    <div className="text-sm text-emerald-700">Client : {savedContract.client_nom} · Montant TTC : {fmt(savedContract.total_ttc)}</div>
                  </div>
                </CardContent>
              </Card>
              <Button onClick={downloadPdf} className="w-full bg-[#0B1E42] hover:bg-[#1e3a8a] text-white gap-2 h-12" data-testid="btn-download-pdf">
                <Download className="h-5 w-5" /> Télécharger le contrat en PDF
              </Button>
              <div className="text-xs text-gray-500 text-center">
                Le contrat est disponible dans le module <b>Suivi &gt; Contrats Conception</b>.
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t bg-slate-50 sticky bottom-0 flex justify-between gap-2">
          {step > 1 && step < 4 && (
            <Button variant="outline" onClick={() => setStep(s => s - 1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Retour
            </Button>
          )}
          <div className="flex-1" />
          {step === 1 && <Button onClick={() => setStep(2)} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="btn-next-step">Suivant <ArrowRight className="h-4 w-4" /></Button>}
          {step === 2 && <Button onClick={() => setStep(3)} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="btn-next-step">Suivant <ArrowRight className="h-4 w-4" /></Button>}
          {step === 3 && (
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="btn-create-contract">
              {saving ? 'Création…' : 'Créer le contrat'} <CheckCircle2 className="h-4 w-4" />
            </Button>
          )}
          {step === 4 && (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="gap-2">Terminer</Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
